import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Loader2, Pin, PinOff, Search, FileText, Save, X } from 'lucide-react';
import { toast } from 'sonner';

export default function NotesPanel({ api }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null); // currently editing/viewing note
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ title: '', body: '', tags: [], pinned: false });

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ceo-life/notes', { params: { q: search } });
      setNotes(data || []);
    } catch { toast.error('Failed to load notes'); }
    setLoading(false);
  }, [api, search]);

  useEffect(() => {
    const t = setTimeout(fetchNotes, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchNotes, search]);

  const startCreate = () => { setCreating(true); setActive(null); setDraft({ title: '', body: '', tags: [], pinned: false }); };

  const openNote = (n) => { setActive(n); setCreating(false); setDraft({ ...n, tags: n.tags || [] }); };

  const save = async () => {
    if (!draft.title.trim() && !draft.body.trim()) { toast.error('Add a title or body'); return; }
    try {
      if (draft.id) {
        const { data } = await api.put(`/ceo-life/notes/${draft.id}`, draft);
        setNotes(prev => prev.map(n => n.id === data.id ? data : n));
        toast.success('Saved');
        setActive(data);
      } else {
        const { data } = await api.post('/ceo-life/notes', draft);
        setNotes(prev => [data, ...prev]);
        setActive(data);
        setCreating(false);
        toast.success('Note created');
      }
    } catch { toast.error('Save failed'); }
  };

  const togglePin = async (note) => {
    try {
      const { data } = await api.put(`/ceo-life/notes/${note.id}`, { pinned: !note.pinned });
      setNotes(prev => prev.map(n => n.id === data.id ? data : n).sort((a, b) => (b.pinned - a.pinned) || (b.updated_at || '').localeCompare(a.updated_at || '')));
      if (active && active.id === note.id) setActive(data);
    } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/ceo-life/notes/${id}`);
      setNotes(prev => prev.filter(n => n.id !== id));
      if (active?.id === id) { setActive(null); setDraft({ title:'', body:'', tags:[], pinned:false }); }
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }}/></div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="ceo-notes" style={{ minHeight: 480 }}>
      {/* List column */}
      <div className="md:col-span-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-low)' }} />
            <input
              className="ceo-input"
              style={{ paddingLeft: 32 }}
              placeholder="Search notes..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="ceo-notes-search"
            />
          </div>
          <button onClick={startCreate} className="ceo-btn ceo-btn-primary" data-testid="ceo-note-new"><Plus className="h-3.5 w-3.5" /></button>
        </div>

        <div className="ceo-card-elevated overflow-hidden" style={{ maxHeight: 500, overflowY: 'auto' }}>
          {notes.length === 0 ? (
            <div className="ceo-empty m-2 border-0 p-6">
              <FileText className="ceo-empty-icon h-8 w-8" />
              <p className="text-xs">{search ? 'No matches.' : 'No notes yet. Click + to begin.'}</p>
            </div>
          ) : notes.map(n => (
            <button
              key={n.id}
              onClick={() => openNote(n)}
              className="w-full text-left px-4 py-3 transition-colors block"
              style={{
                background: active?.id === n.id ? 'var(--ink-3)' : 'transparent',
                borderBottom: '1px solid var(--line)',
                borderLeft: active?.id === n.id ? '2px solid var(--accent)' : '2px solid transparent',
              }}
              data-testid={`ceo-note-${n.id}`}
            >
              <div className="flex items-center gap-2">
                {n.pinned && <Pin className="h-3 w-3" style={{ color: 'var(--accent)' }} />}
                <span className="text-sm font-semibold truncate flex-1" style={{ color: 'var(--text)' }}>{n.title || 'Untitled'}</span>
              </div>
              {n.body && <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--text-dim)' }}>{n.body}</p>}
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px]" style={{ color: 'var(--text-low)' }}>{formatRel(n.updated_at)}</span>
                {(n.tags || []).slice(0, 2).map(t => <span key={t} className="ceo-pill" style={{ padding: '0 6px', fontSize: 9 }}>#{t}</span>)}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Editor column */}
      <div className="md:col-span-8">
        {(active || creating) ? (
          <div className="ceo-card-elevated p-5 space-y-3" data-testid="ceo-note-editor">
            <div className="flex items-center gap-2">
              <input
                className="ceo-input"
                style={{ fontSize: 18, fontWeight: 700, padding: '10px 12px', flex: 1 }}
                placeholder="Title"
                value={draft.title}
                onChange={e => setDraft(p => ({ ...p, title: e.target.value }))}
                data-testid="ceo-note-title"
              />
              {active && (
                <button onClick={() => togglePin(active)} className="ceo-btn ceo-btn-ghost" title={active.pinned ? 'Unpin' : 'Pin to top'} data-testid="ceo-note-pin">
                  {active.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                </button>
              )}
            </div>

            <textarea
              rows={14}
              className="ceo-textarea"
              style={{ fontFamily: 'inherit', lineHeight: 1.7, fontSize: 14 }}
              placeholder="Start writing..."
              value={draft.body}
              onChange={e => setDraft(p => ({ ...p, body: e.target.value }))}
              data-testid="ceo-note-body"
            />

            <div>
              <label className="ceo-eyebrow">Tags (comma separated)</label>
              <input
                className="ceo-input"
                placeholder="strategy, board, hr..."
                value={(draft.tags || []).join(', ')}
                onChange={e => setDraft(p => ({ ...p, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                data-testid="ceo-note-tags"
              />
            </div>

            <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid var(--line)' }}>
              <div className="text-xs" style={{ color: 'var(--text-low)' }}>
                {active ? `Last updated ${formatRel(active.updated_at)}` : 'New note'}
              </div>
              <div className="flex items-center gap-2">
                {active && (
                  <button onClick={() => remove(active.id)} className="ceo-btn ceo-btn-danger" data-testid="ceo-note-delete">
                    <Trash2 className="h-3 w-3" />Delete
                  </button>
                )}
                {creating && (
                  <button onClick={() => setCreating(false)} className="ceo-btn ceo-btn-ghost" data-testid="ceo-note-cancel">
                    <X className="h-3 w-3" />Cancel
                  </button>
                )}
                <button onClick={save} className="ceo-btn ceo-btn-primary" data-testid="ceo-note-save">
                  <Save className="h-3 w-3" />Save
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="ceo-empty h-full flex flex-col items-center justify-center">
            <FileText className="ceo-empty-icon h-10 w-10" />
            <p className="text-sm" style={{ color: 'var(--text-dim)' }}>Select a note or click <strong style={{ color: 'var(--text)' }}>+</strong> to create one.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function formatRel(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff/86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}
